import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../../../ui/dialog";
import { Input } from "../../../../ui/input";
import { EDSButton } from "../../../../eds/EDSButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../ui/select";
import { PreOrderItemFormData } from "../../../../../APIs/Booking/PreOrderOperations";
import { AppContext } from "../../../../../context/AppContext";
import { DiaryContext } from "../../../../../context/DiaryContext";
import { Preorder } from "../../../../../types/bookings";
import { getCurrencySymbol } from "../../../../../utils/number";
import { getBookingTimeConstraints } from "../../../../../utils/booking";
import HtmlEditor from "../Messages/Email/HtmlEditor";
import {
  fetchPreOrderItems,
  PreOrderItem
} from "../../../../../APIs/Venue/GetPreOrderItems";
import { TimePicker } from "../../../../blocks/time-picker";

type AddEditPreOrderItemModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (itemData: PreOrderItemFormData) => Promise<void>;
  existingPreOrder?: Preorder;
  currency: string;
};

const arrayToRecord = (arr?: string[]): Record<string, boolean> => {
  if (!arr) return {};
  return arr.reduce<Record<string, boolean>>(
    (acc, item) => ({ ...acc, [item]: true }),
    {}
  );
};

export const AddEditPreOrderItemModal = ({
  open,
  onOpenChange,
  onSave,
  existingPreOrder,
  currency
}: AddEditPreOrderItemModalProps) => {
  const { venueGroupConfig } = useContext(AppContext);
  const diaryContext = useContext(DiaryContext);
  const portalContainerRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState<PreOrderItemFormData>({
    itemId: "",
    itemName: "",
    itemPrice: 0,
    itemType: "food",
    itemSubType: "",
    forName: "",
    seat: "",
    time: "",
    quantity: 1,
    discountType: "fixed",
    discountAmount: 0,
    description: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [preOrderItems, setPreOrderItems] = useState<PreOrderItem[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);

  // Calculate min/max times from booking start and end times
  const bookingTimeConstraints = getBookingTimeConstraints(
    diaryContext.selectedBookingV4
  );

  useEffect(() => {
    const loadPreOrderItems = async () => {
      if (!diaryContext.selectedVenue) {
        const fallbackItems: PreOrderItem[] =
          venueGroupConfig?.booking_packages?.map((pkg) => ({
            id: pkg.id,
            name: pkg.name,
            price: pkg.price,
            type: pkg.type,
            sub_type: pkg.sub_type,
            description: pkg.description,
            plu: pkg.plu,
            calories: pkg.calories,
            configurable_options: pkg.configurable_options,
            allergens: arrayToRecord(pkg.allergens),
            diet_types: arrayToRecord(pkg.diet_types)
          })) ?? [];
        setPreOrderItems(fallbackItems);
        return;
      }

      setIsLoadingItems(true);
      try {
        const items = await fetchPreOrderItems(diaryContext.selectedVenue);
        if (items.length > 0) {
          setPreOrderItems(items);
        } else {
          const fallbackItems: PreOrderItem[] =
            venueGroupConfig?.booking_packages?.map((pkg) => ({
              id: pkg.id,
              name: pkg.name,
              price: pkg.price,
              type: pkg.type,
              sub_type: pkg.sub_type,
              description: pkg.description,
              plu: pkg.plu,
              calories: pkg.calories,
              configurable_options: pkg.configurable_options,
              allergens: arrayToRecord(pkg.allergens),
              diet_types: arrayToRecord(pkg.diet_types)
            })) ?? [];
          setPreOrderItems(fallbackItems);
        }
      } catch (error) {
        console.error("Error loading pre-order items:", error);
        const fallbackItems: PreOrderItem[] =
          venueGroupConfig?.booking_packages?.map((pkg) => ({
            id: pkg.id,
            name: pkg.name,
            price: pkg.price,
            type: pkg.type,
            sub_type: pkg.sub_type,
            description: pkg.description,
            plu: pkg.plu,
            calories: pkg.calories,
            configurable_options: pkg.configurable_options,
            allergens: arrayToRecord(pkg.allergens),
            diet_types: arrayToRecord(pkg.diet_types)
          })) ?? [];
        setPreOrderItems(fallbackItems);
      } finally {
        setIsLoadingItems(false);
      }
    };

    if (open) {
      loadPreOrderItems();
    }
  }, [open, diaryContext.selectedVenue, venueGroupConfig]);

  // Normalise discount_type from DB to ensure it matches our Select values
  const normaliseDiscountType = (
    value: string | undefined
  ): "fixed" | "percentage" => {
    if (value === "percentage" || value === "percent" || value === "%") {
      return "percentage";
    }
    return "fixed";
  };

  useEffect(() => {
    if (existingPreOrder?.items[0]) {
      const item = existingPreOrder.items[0];
      // Find matching item in preOrderItems by name to get the correct ID for the select
      const matchingItem = preOrderItems.find(
        (poi) => poi.name === item.name && poi.price === item.price
      );
      setFormData({
        itemId: matchingItem?.id ?? item.id,
        itemName: item.name,
        itemPrice: item.price,
        itemType: item.type,
        itemSubType: item.sub_type,
        forName: item.for_name ?? "",
        seat: item.seat ?? "",
        time: item.time ?? "",
        quantity: item.quantity,
        discountType: normaliseDiscountType(item.discount_type),
        discountAmount: item.discount_amount ?? 0,
        description: item.description ?? ""
      });
    } else {
      setFormData({
        itemId: "",
        itemName: "",
        itemPrice: 0,
        itemType: "food",
        itemSubType: "",
        forName: "",
        seat: "",
        time: "",
        quantity: 1,
        discountType: "fixed",
        discountAmount: 0,
        description: ""
      });
    }
  }, [existingPreOrder, open, preOrderItems]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.itemName) {
      newErrors.itemName = "Item is required";
    }

    if (!formData.quantity || formData.quantity <= 0) {
      newErrors.quantity = "Quantity must be greater than 0";
    }

    if (formData.itemPrice < 0) {
      newErrors.itemPrice = "Price must be 0 or greater";
    }

    // Validate discount doesn't exceed price or 100%
    if (formData.discountAmount && formData.discountAmount > 0) {
      const baseTotal = formData.quantity * formData.itemPrice;
      if (formData.discountType === "percentage") {
        if (formData.discountAmount > 100) {
          newErrors.discountAmount = "Discount cannot exceed 100%";
        }
      } else if (formData.discountAmount > baseTotal) {
        newErrors.discountAmount = "Discount cannot exceed the total price";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData);
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving pre-order item:", error);
      setErrors({ submit: "Failed to save pre-order item. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof PreOrderItemFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleItemSelect = (itemId: string) => {
    const selectedItem = preOrderItems.find((item) => item.id === itemId);
    if (selectedItem) {
      setFormData((prev) => ({
        ...prev,
        itemId: selectedItem.id,
        itemName: selectedItem.name,
        itemPrice: selectedItem.price,
        itemType: selectedItem.type,
        itemSubType: selectedItem.sub_type
      }));
      // Clear the item error when a new item is selected
      if (errors.itemName) {
        setErrors((prev) => ({ ...prev, itemName: "" }));
      }
    }
  };

  const calculateTotal = () => {
    const baseTotal = formData.quantity * formData.itemPrice;
    let discountValue = 0;

    if (formData.discountAmount) {
      if (formData.discountType === "percentage") {
        discountValue = (baseTotal * formData.discountAmount) / 100;
      } else {
        discountValue = formData.discountAmount;
      }
    }

    return baseTotal - discountValue;
  };

  const renderSelectContent = () => {
    if (isLoadingItems) {
      return (
        <div className="p-2 text-sm text-muted-foreground">
          Loading items...
        </div>
      );
    }

    if (preOrderItems.length === 0) {
      return (
        <div className="p-2 text-sm text-muted-foreground">
          No items available
        </div>
      );
    }

    return preOrderItems.map((item) => (
      <SelectItem key={item.id} value={item.id}>
        {item.name} - {getCurrencySymbol(currency)}
        {item.price.toFixed(2)}
      </SelectItem>
    ));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="md:max-w-[700px] md:max-h-[90vh] overflow-y-auto overflow-x-hidden"
        ref={portalContainerRef}
      >
        <DialogHeader>
          <DialogTitle>
            {existingPreOrder ? "Edit Pre-Order Item" : "Add Pre-Order Item"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <label htmlFor="item-select" className="font-medium text-sm">
              Item *
            </label>
            <Select value={formData.itemId} onValueChange={handleItemSelect}>
              <SelectTrigger id="item-select">
                <SelectValue placeholder="Select an item" />
              </SelectTrigger>
              <SelectContent
                position="popper"
                sideOffset={4}
                container={portalContainerRef.current}
              >
                {renderSelectContent()}
              </SelectContent>
            </Select>
            {errors.itemName && (
              <span className="text-sm text-red-600">{errors.itemName}</span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="for-name" className="font-medium text-sm">
                For (name)
              </label>
              <Input
                id="for-name"
                value={formData.forName}
                onChange={(e) => handleInputChange("forName", e.target.value)}
                placeholder="Guest name"
                autoComplete="off"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="seat" className="font-medium text-sm">
                Seat
              </label>
              <Input
                id="seat"
                value={formData.seat}
                onChange={(e) => handleInputChange("seat", e.target.value)}
                placeholder="Seat number"
                autoComplete="off"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="time" className="font-medium text-sm">
                Time
              </label>
              <TimePicker
                value={formData.time}
                onChange={(value) => handleInputChange("time", value)}
                format="24h"
                minuteStep={5}
                placeholder="Select time"
                portalContainer={portalContainerRef.current}
                minTime={bookingTimeConstraints.minTime}
                maxTime={bookingTimeConstraints.maxTime}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="quantity" className="font-medium text-sm">
                Quantity *
              </label>
              <Input
                id="quantity"
                type="number"
                value={formData.quantity}
                onChange={(e) =>
                  handleInputChange("quantity", parseInt(e.target.value) || 1)
                }
                min="1"
                autoComplete="off"
              />
              {errors.quantity && (
                <span className="text-sm text-red-600">{errors.quantity}</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="price" className="font-medium text-sm">
                Price each
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm">{getCurrencySymbol(currency)}</span>
                <Input
                  id="price"
                  type="number"
                  value={formData.itemPrice}
                  disabled
                  className="bg-muted"
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="discount-type" className="font-medium text-sm">
                Discount
              </label>
              <div className="flex gap-2">
                <Select
                  value={formData.discountType}
                  onValueChange={(value) =>
                    handleInputChange("discountType", value)
                  }
                >
                  <SelectTrigger className="w-16 shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent
                    position="popper"
                    sideOffset={4}
                    container={portalContainerRef.current}
                  >
                    <SelectItem value="fixed">
                      {getCurrencySymbol(currency)}
                    </SelectItem>
                    <SelectItem value="percentage">%</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={
                    formData.discountAmount === 0 ? "" : formData.discountAmount
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty string or valid decimal numbers
                    if (value === "" || /^\d*\.?\d*$/.test(value)) {
                      handleInputChange(
                        "discountAmount",
                        value === "" ? 0 : parseFloat(value) || 0
                      );
                    }
                  }}
                  placeholder="0"
                  autoComplete="off"
                />
              </div>
              {errors.discountAmount && (
                <span className="text-sm text-red-600">
                  {errors.discountAmount}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 p-3 bg-muted rounded-md">
            <div className="flex justify-between items-center">
              <span className="font-medium text-sm">Total value:</span>
              <span className="font-semibold text-lg">
                {getCurrencySymbol(currency)}
                {calculateTotal().toFixed(2)}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="description" className="font-medium text-sm">
              Description / Notes
            </label>
            <div className="border rounded-md">
              <HtmlEditor
                body={formData.description ?? ""}
                onEditorUpdate={(body) =>
                  handleInputChange("description", body)
                }
                readOnly={false}
                placeholder="Enter description / notes"
              />
            </div>
          </div>

          {errors.submit && (
            <div className="text-sm text-red-600 p-3 bg-red-50 rounded-md">
              {errors.submit}
            </div>
          )}
        </div>

        <DialogFooter>
          <EDSButton
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </EDSButton>
          <EDSButton
            variant="default"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Done"}
          </EDSButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditPreOrderItemModal;
